import {Component, ComponentFactoryResolver, Input, ViewChild, ViewContainerRef} from '@angular/core';
import {FormBuilder, NgForm, UntypedFormGroup, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {BaseGridNoCookieComponent} from 'app/core-ui/abstract/basegrid/basegridnocookie.component';
import {EventsEmitterService} from 'app/core-ui/abstract/services/emitter.service';
import {LogService} from 'app/core-ui/abstract/services/log.service';
import {SecurityService} from 'app/core-ui/abstract/services/security.service';
import {VisualsService} from 'app/core-ui/abstract/services/visuals.service';
import * as FileSaver from 'file-saver';
import {ModalDirective} from 'ngx-bootstrap/modal';
import {ComponentRegistryService} from './component-registry.service';
import {RequestsForApprovalService} from './requestsForApproval.service';

@Component({
  selector: 'requestsForApproval',
  templateUrl: 'requestsForApproval.component.html',
  providers: [RequestsForApprovalService],
})
export class RequestsForApproval extends BaseGridNoCookieComponent<RequestsForApprovalService> {
  @ViewChild('dynamicComponentContainer', {read: ViewContainerRef, static: false})
  container!: ViewContainerRef;
  @ViewChild('confirmModal', {static: false}) public confirmModal: ModalDirective;
  @ViewChild('rejectModal', {static: false}) public rejectModal: ModalDirective;

  public loading = false;
  public group = 'requestsForApproval';
  @Input() public selectorName : string;
  isSubmitted: boolean = false;
  rejectionForm: UntypedFormGroup;
  rejectionReasonValue: string;
  tempParams: any;

  constructor(
    public requestsForApprovalService: RequestsForApprovalService,
    public componentRegistry: ComponentRegistryService,
    private resolver: ComponentFactoryResolver,
    public eventsEmitterService: EventsEmitterService,
    public securityService: SecurityService,
    logger: LogService,
    public fb: FormBuilder,
    visualsService: VisualsService,
    public route: ActivatedRoute,
    public router: Router,
  ) {
    super(
      securityService,
      logger,
      visualsService,
      requestsForApprovalService,
      'requestsForApproval',
    );
    this.buildForm();
    this.loadVisuals();
    this.eventsEmitterService.languageChange.subscribe((langId) => {
      this.loadVisuals();
    });
  }

  ngOnInit() {
    this.selectorName != null ? this.initParams = {selectorName : this.selectorName} : null; 
    this.search();
  }

  buildForm() {
    this.rejectionForm = this.fb.group({
      rejectionReason: ['', [Validators.required,Validators.maxLength(250)]],
    });
  }

  doApprove(params) {
    let requestId = {'requestId': Number(params.requestId)};
    this.requestsForApprovalService.approveRequest(requestId).subscribe(
      (data) => {
        if (data.success) {
          LogService.emitAlert({
            type: 'success',
            body: data.message,
          });
          this.search();
        } else {
          LogService.emitAlert({
            type: 'error',
            body: data.message,
          });
          if (data.data.foureyesType == 'FILE' && data.data.statusCode == 415) {
            let newJsonRec = JSON.parse(params['newJsonRec']);
            let filename = 'error_' + newJsonRec.fileName;
            this.downloadFile(data.data.data, newJsonRec.mimeType, filename);
          }
        }
      },
      (error) => {
        this.logger.info(error);
      },
    );
  }

  doReject(params) {
    let rejectionRequest = {'requestId': params.requestId,'rejectionReason': params.rejectionReason};
    this.requestsForApprovalService.rejectRequest(rejectionRequest).subscribe({
      next: (data) => this.handleSuccess(data.message),
      error: (error) => this.handleError(error),
    });
  }
  doRejectByMaker(params) {
    let requestId = {'requestId': params.requestId};
    this.requestsForApprovalService.rejectByMakerRequest(requestId).subscribe(
      (data) => {
        LogService.emitAlert({
          type: 'success',
          body: data.message,
        });
        this.search();
      },
      (error) => {},
    );
  }
  goToEdit(params) {
    if (params['type'] == 'FILE') {
      let newJsonRec = JSON.parse(params['newJsonRec']);
      this.downloadFile(newJsonRec.file, newJsonRec.mimeType, newJsonRec.fileName);
    } else if (params['type'] == 'FORM') {
      this.createComponent(params);
    }
  }

  createComponent(row) {
    this.container.clear();
    this.confirmModal.show();
    const component = this.componentRegistry.getComponent(row['selectorName']);
    let foureyesParams = JSON.parse(row['params']);
    if (component) {
      const componentFactory = this.resolver.resolveComponentFactory(component);
      const componentRef = this.container.createComponent(componentFactory);
      componentRef.instance['fourEyes'] = true;
      componentRef.instance['fourEyesRequestId'] = row['requestId'];
      componentRef.instance['foureyesComparisonId'] = foureyesParams.foureyesComparisonId;
    }
  }

  downloadFile(base64: string, mimetype: string, filename: string) {
    var blob = 'data:' + mimetype + ';base64,' + base64;
    FileSaver.saveAs(blob, filename);
  }

  private openRejectionPopup(params) {
    this.isSubmitted = false;
    this.tempParams = params;
    this.rejectModal.show();
  }
  
  onSubmit(rejectionReasonValue?: NgForm): void {
    this.isSubmitted = true;
  
    if (!this.rejectionForm.valid) {
      console.log('Form is invalid');
      return;
    }
  
    this.tempParams.rejectionReason = this.rejectionForm.value.rejectionReason;
    this.doReject(this.tempParams);
  }
  
  private handleSuccess(message: string): void {
    LogService.emitAlert({ type: 'success', body: message });
    this.resetFormState();
  }
  
  private handleError(error: any): void {
    console.error('Error:', error);
    LogService.emitAlert({ type: 'error', body: 'An error occurred during rejection.' });
    this.resetFormState();
  }
  
  private resetFormState(): void {
    this.isSubmitted = false;
    this.rejectionForm.reset();
    this.rejectModal.hide();
    this.search();
  }
}
